import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Parser from 'html-react-parser';
import moment from 'moment';
import {
  Button,
  Carousel,
  CarouselControl,
  CarouselIndicators,
  CarouselItem,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import { showModal } from '../../actions';
import { setUserSetting } from '../../actions/Auth';

import { APIupdateUserSettingValue } from '../../api/User';

import {
  getSetting,
  getUserSetting,
} from '../../helpers/Settings';

const DashboardWidgetCarousel = ({ profile, openModal, updateSetting }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  const dismissed = getUserSetting('dashboard_carousel_dismissed');
  const items = (getSetting('dashboard_carousel_items') || [])
    .filter(i => !dismissed.includes(i.id))
    .filter(i => {
      const start = i.startDateTime ? moment(i.startDateTime) : moment('2025-01-01 00:00:00');
      const end = i.endDateTime ? moment(i.endDateTime) : moment('2222-01-01 00:00:00');
      return moment().isBetween(start, end, 'seconds', '[]');
    });

  const intl = useIntl();

  const next = () => {
    if (animating) return;
    const nextIndex = activeIndex === items.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(nextIndex);
  };

  const previous = () => {
    if (animating) return;
    const nextIndex = activeIndex === 0 ? items.length - 1 : activeIndex - 1;
    setActiveIndex(nextIndex);
  };

  const goToIndex = (newIndex) => {
    if (animating) return;
    setActiveIndex(newIndex);
  };

  const handleDismiss = async (id) => {
    const response = await APIupdateUserSettingValue(profile.uuid, 'dashboard_carousel_dismissed', id);
    updateSetting({ dashboard_carousel_dismissed: response.data });
  };

  const handleOpenModal = (url, title) => {
    openModal(url, title);
  };

  const slides = items.map((item) => (
    <CarouselItem
      key={item.id}
      className={`carousel-item-${item.id}`}
      onExiting={() => setAnimating(true)}
      onExited={() => setAnimating(false)}
    >
      {item.dismissable !== false && (
        <Button className="carousel-dismiss" onClick={() => handleDismiss(item.id)} style={{ color: item.textColor }}>
          <span className="carousel-dismiss-note">{intl.formatMessage({ defaultMessage: 'Dismiss' })}</span>
          <i className="fal fa-circle-x" />
        </Button>
      )}
      <div className="carousel-content">
        <div className="carousel-col">
          {item.title && (
            <h3 style={{ color: item.textColor }}>
              {item.title}
            </h3>
          )}
          {item.text && (
            <p className="carousel-text" style={{ color: item.textColor }}>
              {Parser(item.text)}
            </p>
          )}
          {item.buttons.length > 0 && (
            <div className="carousel-buttons">
              {item.buttons.map((button, idx) => (
                <Fragment key={`${item.id}-${idx + 1}`}>
                  {button.link && (
                    <Button color="link" tag={Link} to={button.link} style={{ color: button.textColor, backgroundColor: button.backgroundColor }}>
                      {button.text}
                    </Button>
                  )}
                  {button.url && (
                    <>
                      {button.modal ? (
                        <Button color="link" tag={Link} onClick={(e) => { e.preventDefault(); handleOpenModal(button.url, item.title); }} style={{ color: button.textColor, backgroundColor: button.backgroundColor }}>
                          {button.text}
                        </Button>
                      ) : (
                        <Button color="link" tag="a" href={button.url} target="_blank" style={{ color: button.textColor, backgroundColor: button.backgroundColor }}>
                          {button.text}
                        </Button>
                      )}
                    </>
                  )}
                </Fragment>
              ))}
            </div>
          )}
        </div>
        {item.image && (
          <div className="carousel-col">
            <img src={item.image} alt={item.title} />
          </div>
        )}
      </div>
      {item.backgroundImage && (
        <div className="carousel-overlay" />
      )}
      <style>
        {`
        .carousel-item-${item.id} {
          color: ${item.textColor};
          background-color: ${item.backgroundColor};
          ${item.backgroundImage && `
            background-image: url('${item.backgroundImage}');
            background-position: center;
            background-repeat: no-repeat;
            background-size: cover;
          `}
        }
        `}
      </style>
    </CarouselItem>
  ));

  if (items.length === 0) return null;

  return (
    <div className="dashboard-carousel">
      <Carousel
        activeIndex={activeIndex}
        next={next}
        previous={previous}
      >
        {items.length > 1 && (
          <CarouselIndicators
            items={items}
            activeIndex={activeIndex}
            onClickHandler={goToIndex}
          />
        )}
        {slides}
        <CarouselControl
          direction="prev"
          directionText="Previous"
          onClickHandler={previous}
        />
        <CarouselControl
          direction="next"
          directionText="Next"
          onClickHandler={next}
        />
      </Carousel>
    </div>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

const mapDispatchToProps = dispatch => ({
  openModal: (url, title) => {
    dispatch(showModal('VideoPlayerModal', {
      className: 'modal-video-player',
      url,
      title,
      backdrop: true,
    }));
  },
  updateSetting: (setting) => {
    dispatch(setUserSetting(setting));
  },
});

DashboardWidgetCarousel.propTypes = {
  profile: PropTypes.object,
  openModal: PropTypes.func.isRequired,
  updateSetting: PropTypes.func.isRequired,
};

DashboardWidgetCarousel.defaultProps = {
  profile: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardWidgetCarousel);
